import React, { useCallback } from 'react';

import { useForm, FormProvider } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button, Box, Backdrop, CircularProgress } from '@mui/material';
import { CatalogApiFull } from '@europrocurement/l2d-domain';
import {
    CatalogueJsonldCatalogueReadCatalogueReadGrilleTarifaireCatalogueReadFull,
    TblRelArticleJsonldCatalogueReadCatalogueReadGrilleTarifaireCatalogueReadFull,
    TblRelPrescripteurArticleJsonldCatalogueReadCatalogueReadGrilleTarifaireCatalogueReadFull,
    TblSurchargeArticlePackageJsonldCatalogueReadCatalogueReadGrilleTarifaire,
} from '@europrocurement/l2d-domain/openApi/ApiOffre';
import { enqueueSnackbar } from 'notistack';
import { ModelForm } from '../../forms/types';
import {
    ArticlesSchema,
    articlesStepSchema,
    ConfigurationSchema,
} from '../../Package/Form/validation';
import { apiArticlesToFormEditableArticles } from '../../Article/format';
import { SortableList, SortableItem } from '../../components/SortableList';
import ArticleLine from '../../Package/Components/ArticleLine';
import useArticlesModuleConstraints from '../../Package/Hook/useArticlesModuleConstraints';
import useOffreForm, {
    OffrePrescripteurRelArticle,
} from '../../../GrilleTarifaire/hook/useOffreForm';
import { ArticleFormItem } from '../../Package/Components/types';

type FormProps = {
    entity: CatalogApiFull;
    idCatalogue: number;
    idPrescriber: number;
    isCreate: boolean;
    afterSubmit: () => void;
};

export function getRelationIdFromArticleId(relations, articleId) {
    const foundRelation = relations.find((rel) => rel.article?.id === articleId);
    return foundRelation?.id ?? null;
}

export function appliquerSurchargesParPriorite(
    idPrescriber: number,
    relationArticles: TblRelArticleJsonldCatalogueReadCatalogueReadGrilleTarifaireCatalogueReadFull[],
    surchargeCatalogue: TblSurchargeArticlePackageJsonldCatalogueReadCatalogueReadGrilleTarifaire[] = [],
    surchargePrescripteur: TblRelPrescripteurArticleJsonldCatalogueReadCatalogueReadGrilleTarifaireCatalogueReadFull[] = [],
) {
    return relationArticles.map((relation) => {
        const idArticle = relation.article?.id;
        const surcharge =
            surchargePrescripteur.find((s) => {
                const prescripteurId = parseInt(s.prescripteur.split('/').pop() ?? '', 10);
                return s.article?.article?.id === idArticle && prescripteurId === idPrescriber;
            }) ?? surchargeCatalogue.find((s) => s.relationArticle?.article?.id === idArticle);

        if (surcharge) {
            return {
                ...relation,
                tarif: surcharge.tarif,
            };
        }

        return relation;
    });
}

const PrescriberArticleOverrideForm: React.FunctionComponent<
    ModelForm<CatalogueJsonldCatalogueReadCatalogueReadGrilleTarifaireCatalogueReadFull>
> = function ({ entity, idCatalogue, idPrescriber, isCreate, afterSubmit }: FormProps) {
    // Form configuration

    /** Creation form | Default values  */
    const getDefaultValues = () => ({
        label: '',
        commercialName: '',
        domainId: 0,
        formalityId: null,
        articles: [],
        actions: [],
        additionalFields: [],
        documents: [],
        readOnlyDocuments: {
            articles: [],
            options: [],
            formalities: [],
        },
    });
    const [loading, setLoading] = React.useState(false);

    const domainId = entity?.package?.domaine?.id ?? 0;

    const { getWarnings } = useArticlesModuleConstraints();
    const { createSurchargeArticle, updateSurchargeArticle, deleteSurchargeArticle } =
        useOffreForm();

    const getConfiguration = (): ConfigurationSchema => ({
        label: entity?.package?.libelle ?? '',
        commercialName: entity?.package?.nomCommercial ?? '',
        domainId,
        formalityId: entity?.package?.formalite?.id,
    });

    const articlesVac = (entity?.package?.articles ?? []).filter(
        (item) => item.article?.rubArticle === 'VAC',
    );
    const listArticles = appliquerSurchargesParPriorite(
        idPrescriber,
        articlesVac,
        entity.surchargeArticles,
        entity.surchargePrescripteur,
    );

    /** Update form | Default values from api  */
    const getPackageFromEntity = () => ({
        ...getConfiguration(),
        ...apiArticlesToFormEditableArticles(listArticles ?? []),
    });

    const formValues = isCreate ? getDefaultValues() : getPackageFromEntity();

    const formContext = useForm({
        mode: 'onBlur',
        defaultValues: { ...formValues },
        resolver: zodResolver(articlesStepSchema),
    });

    const { articles } = formContext.getValues();
    const setArticles = (values: ArticlesSchema['articles']) => {
        formContext.setValue('articles', values);
    };

    const treatmentArticle = useCallback(() => {
        const promises: Promise<OffrePrescripteurRelArticle[]>[] = articles
            .filter((item) => listArticles.some((rel) => rel.article?.id === item.articleId))
            .map((item) => {
                if (item.price === undefined || item.price === '0' || item.price === '') {
                    return undefined;
                }
                setLoading(true);
                const relation = listArticles.find((rel) => rel.article?.id === item.articleId);
                const surchargeExist =
                    (entity?.surchargePrescripteur ?? []).find((s) => {
                        const prescripteurId = parseInt(s.prescripteur.split('/').pop() ?? '', 10);
                        return s.article?.id === relation?.id && idPrescriber === prescripteurId;
                    }) ?? undefined;
                if (surchargeExist && surchargeExist.id) {
                    return updateSurchargeArticle(surchargeExist.id as unknown as string, {
                        article: relation?.id as unknown as string,
                        catalogue: idCatalogue as unknown as string,
                        tarif: item.price,
                        prescripteur: idPrescriber as unknown as string,
                    });
                }
                return createSurchargeArticle({
                    article: relation?.id as unknown as string,
                    catalogue: idCatalogue as unknown as string,
                    tarif: item.price,
                    prescripteur: idPrescriber as unknown as string,
                });
            })
            .filter(
                (promise): promise is Promise<OffrePrescripteurRelArticle[]> =>
                    promise !== undefined,
            );

        Promise.all(promises)
            .then(() => {
                afterSubmit();
                setLoading(false);
                enqueueSnackbar('Mise à jour effectuée avec succès', { variant: 'success' });
            })
            .catch((err) => {
                console.log(err);
                afterSubmit();
                setLoading(false);
                enqueueSnackbar('Une erreur est survenue', { variant: 'error' });
            });
    }, [
        afterSubmit,
        articles,
        listArticles,
        idCatalogue,
        idPrescriber,
        createSurchargeArticle,
        entity.surchargePrescripteur,
        updateSurchargeArticle,
    ]);

    const treatmentArticleDelete = (event: React.MouseEvent<HTMLElement>) => {
        event.preventDefault();
        setLoading(true);

        const promises: Promise<OffrePrescripteurRelArticle[]>[] = (
            entity?.surchargePrescripteur ?? []
        ).map((item) => {
            const prescripteurId = parseInt(item.prescripteur.split('/').pop() ?? '', 10);
            if (idPrescriber === prescripteurId) {
                return deleteSurchargeArticle(item?.id as unknown as string);
            }
            return Promise.resolve([]);
        });

        Promise.all(promises)
            .then(() => {
                afterSubmit();
                setLoading(false);
                enqueueSnackbar('Mise à jour effectuée avec succès', { variant: 'success' });
            })
            .catch((err) => {
                console.log(err);
                afterSubmit();
                setLoading(false);
                enqueueSnackbar('Une erreur est survenue', { variant: 'error' });
            });
    };

    const {
        formState: { errors },
        handleSubmit,
    } = formContext;
    const stringIndexes = Object.keys(errors.articles ?? {});
    const errorIndexes = stringIndexes.map((stringIndex) => parseInt(stringIndex, 10));

    const innerHandleSubmit = () => handleSubmit(treatmentArticle);

    return (
        <>
            <Backdrop
                sx={(backDropTheme) => ({
                    color: 'common.white',
                    zIndex: backDropTheme.zIndex.drawer + 1,
                })}
                open={loading}
            >
                <CircularProgress color="inherit" />
            </Backdrop>
            <FormProvider {...formContext}>
                <form
                    noValidate
                    onSubmit={innerHandleSubmit()}
                >
                    <SortableList
                        items={articles}
                        onChange={setArticles}
                        renderItem={(article: ArticleFormItem, index: number) => {
                            const hasErrorOnValidation = errorIndexes.includes(index);
                            return (
                                <SortableItem
                                    id={article.id}
                                    key={`package_content_item_${article.id}`}
                                >
                                    <ArticleLine
                                        article={article}
                                        index={index}
                                        context={formContext}
                                        hasErrorOnValidation={hasErrorOnValidation}
                                        warnings={getWarnings(article.id, articles)}
                                        disabled
                                        isOptionForm={false}
                                    />
                                </SortableItem>
                            );
                        }}
                    />
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'row',
                            gap: '8px',
                        }}
                    >
                        <Button
                            type="submit"
                            variant="contained"
                        >
                            Valider
                        </Button>
                        <Button
                            onClick={treatmentArticleDelete}
                            variant="contained"
                            color="error"
                        >
                            Supprimer les surcharges
                        </Button>
                    </Box>
                </form>
            </FormProvider>
        </>
    );
};

export default PrescriberArticleOverrideForm;
